import React, { ReactNode } from 'react';
import { action, makeObservable, observable } from 'mobx';
import i18next from 'i18next';

import { IconType } from '@components/Icon';

import { translate } from '@chaika/i18n/helper';

import { ItemMenu } from '@store/abstract/ItemMenu';
import { EntryPageOpenerTestAttributes } from 'chaika-test-attributes';
import { EditNoteCaption } from '@patient/ui/shared/RightMenu/EditNoteCaption';
import { assertIsDefined } from '@utils/assertIsDefined';

export interface MenuItemParams {
    title: string;
    mode: PageMode;
    isAlwaysOpened?: boolean;
    iconType: IconType;
    indicator?: boolean;
    dataTestAttr?: EntryPageOpenerTestAttributes;
    caption?: () => ReactNode | string;
}

export enum PageMode {
    Edit = 'edit',
    View = 'view',
    Template = 'template',
    History = 'history',
    Version = 'version',
    InsurancePrograms = 'insurancePrograms',
    Files = 'files',
    EditNote = 'editNote',
    Transcription = 'transcription',
    Episodes = 'episodes',
    Approval = 'approval',
    ServiceInformedConsent = 'serviceInformedConsent',
}

export class RightMenu extends ItemMenu<MenuItemParams, PageMode> {
    @observable private hasFiles_: boolean = false;
    @observable private signedMode_: boolean = false;

    constructor() {
        super();
        makeObservable(this);

        this.translateItems();
        i18next.on('languageChanged', () => this.translateItems());
    }

    public set isSoapPage(isSoap: boolean) {
        if (isSoap) {
            this.items = this.soapItemList;
        } else {
            this.items = this.defaultItemList;
        }
    }

    public set signedMode(signedMode: boolean) {
        if (signedMode !== this.signedMode_) {
            this.signedMode_ = signedMode;

            this.items = signedMode
                ? this.defaultItemList.filter(item =>
                      [PageMode.Files, PageMode.EditNote, PageMode.ServiceInformedConsent, PageMode.Approval].includes(
                          item.mode,
                      ),
                  )
                : this.defaultItemList;
        }
    }

    public set hasFiles(hasFiles: boolean) {
        if (hasFiles !== this.hasFiles_) {
            this.hasFiles_ = hasFiles;

            const fileItem = this.itemsMap.get(PageMode.Files);
            assertIsDefined(fileItem, 'Right menu always has the files item');
            const fileItemWithIndicator = { ...fileItem, indicator: this.hasFiles_ };
            this.itemsMap.set(PageMode.Files, fileItemWithIndicator);
        }
    }

    @action
    public setSignedForSoap(signedMode: boolean) {
        if (signedMode !== this.signedMode_) {
            this.signedMode_ = signedMode;

            const signedFilterList = signedMode
                ? [
                      PageMode.Episodes,
                      PageMode.Files,
                      PageMode.EditNote,
                      PageMode.Transcription,
                      PageMode.ServiceInformedConsent,
                      PageMode.Approval,
                  ]
                : undefined;

            this.items =
                signedMode && signedFilterList
                    ? this.soapItemList.filter(item => signedFilterList.includes(item.mode))
                    : this.soapItemList;
        } else {
            this.items = this.soapItemList;
        }
    }

    protected getItemsWithIndicators() {
        return this.items.filter(item => {
            return item.mode !== PageMode.Files ? item : { ...item, indicator: this.hasFiles_ };
        });
    }

    protected setItem(item: MenuItemParams, index: number | PageMode) {
        this.itemsMap.set(item.mode, item);
    }

    private get soapItemList(): MenuItemParams[] {
        const items = [
            {
                title: translate('medicalAssistanceEpisodesShort'),
                mode: PageMode.Episodes,
                isAlwaysOpened: true,
                iconType: IconType.ATTACHED,
                dataTestAttr: EntryPageOpenerTestAttributes.Episodes,
            },
            ...this.defaultItemList,
        ];

        const noteItemIndex = items.findIndex(item => item.mode === PageMode.EditNote);
        items.splice(noteItemIndex + 1, 0, {
            title: translate('transcriptionTitle'),
            mode: PageMode.Transcription,
            iconType: IconType.ATTACHED,
            dataTestAttr: EntryPageOpenerTestAttributes.TranscriptionOpener,
        });

        return items;
    }

    private get defaultItemList(): MenuItemParams[] {
        return [
            {
                title: translate('noteTitle'),
                caption: () => React.createElement(EditNoteCaption),
                mode: PageMode.EditNote,
                iconType: IconType.ATTACHED,
                dataTestAttr: EntryPageOpenerTestAttributes.NotesOpener,
            },
            {
                title: translate('templateTitle'),
                mode: PageMode.Template,
                iconType: IconType.TEMPLATE,
                dataTestAttr: EntryPageOpenerTestAttributes.TemplatesOpener,
            },
            {
                title: translate('filesAndLinksTitle'),
                mode: PageMode.Files,
                iconType: IconType.ATTACHED,
                dataTestAttr: EntryPageOpenerTestAttributes.FilesOpener,
            },
            {
                title: translate('insurancePrograms'),
                mode: PageMode.InsurancePrograms,
                iconType: IconType.ATTACHED,
                dataTestAttr: EntryPageOpenerTestAttributes.FilesOpener,
            },
            {
                title: translate('serviceInformedConsentTitle'),
                mode: PageMode.ServiceInformedConsent,
                iconType: IconType.ATTACHED,
                dataTestAttr: EntryPageOpenerTestAttributes.ServiceInformedConsent,
            },
            {
                title: translate('approvalOfAssignedServicesWithTheCompany'),
                mode: PageMode.Approval,
                iconType: IconType.ATTACHED,
                dataTestAttr: EntryPageOpenerTestAttributes.NotesOpener,
            },
        ];
    }

    private translateItems() {
        this.setItems(this.defaultItemList);
    }
}
